import type { ProcessedReportInfo } from '../../types/reporting';

export const REPORT_GRID_PAGE_SIZE = 100;

export type ReportSortModelItem = {
  colId: string;
  sort: 'asc' | 'desc';
  sortIndex?: number;
};

export function encodeGridState(value: unknown): string | null {
  if (!value || (Array.isArray(value) && value.length === 0) || (!Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0)) {
    return null;
  }
  return btoa(encodeURIComponent(JSON.stringify(value)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

export function decodeGridState<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
    return JSON.parse(decodeURIComponent(atob(padded))) as T;
  } catch (error) {
    console.warn('Invalid report grid URL state:', error);
    return fallback;
  }
}

export function encodeSortState(sortModel: ReportSortModelItem[]): string | null {
  if (sortModel.length === 0) return null;
  return sortModel.map((item) => `${encodeURIComponent(item.colId)}:${item.sort}`).join(',');
}

export function decodeSortState(value: string | null): ReportSortModelItem[] {
  if (!value) return [];
  const sortModel: ReportSortModelItem[] = [];
  value.split(',').forEach((part) => {
    const [encodedColId, sort] = part.split(':');
    if (!encodedColId || (sort !== 'asc' && sort !== 'desc')) return;
    sortModel.push({ colId: decodeURIComponent(encodedColId), sort, sortIndex: sortModel.length });
  });
  return sortModel;
}

export function paramValuesForReport(
  report: ProcessedReportInfo,
  initialParamValues?: Record<string, string>,
): Record<string, string> {
  const values: Record<string, string> = {};
  report.parameters.forEach((parameter) => {
    if (initialParamValues?.[parameter.url_key] != null) values[parameter.url_key] = initialParamValues[parameter.url_key];
    else if (parameter.default_value) values[parameter.url_key] = parameter.default_value;
  });

  const dateParams = report.parameters.filter((parameter) => {
    const dataType = parameter.data_type.toLowerCase();
    return dataType.includes('date') || dataType.includes('timestamp');
  });
  const start = dateParams.find((parameter) => ['start_date', 'from_date', 'date_start'].includes(parameter.url_key) || parameter.url_key.includes('start'));
  const end = dateParams.find((parameter) => ['end_date', 'to_date', 'date_end'].includes(parameter.url_key) || parameter.url_key.includes('end'));
  if (start && end) {
    values[start.url_key] ||= '1900-01-01';
    values[end.url_key] ||= '2999-12-31';
  }
  return values;
}

